// Date formatting utilities

// Define ProcessedSet interface locally to avoid dependency
interface ProcessedSet {
	end_time: string;
}

/**
 * Format festival date range for display
 * @param startDate Festival start date
 * @param endDate Festival end date
 * @returns Formatted date range
 */
export function formatFestivalDates(
	startDate: string,
	endDate: string,
): string {
	if (!startDate || !endDate) return "Dates TBD";

	try {
		// Parse dates without timezone conversion - treat as local dates
		const [startYear, startMonth, startDay] = startDate.split("-").map(Number);
		const [endYear, endMonth, endDay] = endDate.split("-").map(Number);

		// Ensure all values are valid numbers
		if (
			!startYear ||
			!startMonth ||
			!startDay ||
			!endYear ||
			!endMonth ||
			!endDay
		) {
			return "Invalid date";
		}

		const start = new Date(startYear, startMonth - 1, startDay);
		const end = new Date(endYear, endMonth - 1, endDay);

		// Check if it's a single day festival (same date)
		if (startDate === endDate) {
			// Single day: "May 8, 2025"
			return start.toLocaleDateString("en-US", {
				month: "long",
				day: "numeric",
				year: "numeric",
			});
		}

		// If same month, show "Month Day-Day, Year"
		if (
			start.getMonth() === end.getMonth() &&
			start.getFullYear() === end.getFullYear()
		) {
			return `${start.toLocaleDateString("en-US", { month: "long" })} ${start.getDate()}-${end.getDate()}, ${start.getFullYear()}`;
		}

		// Different months
		return `${start.toLocaleDateString("en-US", { month: "long", day: "numeric" })} - ${end.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}`;
	} catch {
		return "Date format error";
	}
}

/**
 * Format join date to "Joined Mar 2025" format
 * @param dateString Join date string
 * @returns Formatted join date
 */
export function formatJoinDate(dateString: string): string {
	const date = new Date(dateString);
	return `Joined ${date.toLocaleDateString("en-US", {
		month: "short",
		year: "numeric",
	})}`;
}

/**
 * Check if a set is upcoming (including 1-day grace period)
 * A set is considered upcoming if:
 * - It has no end_time (lineup-only festival)
 * - Its end_time is today or in the future
 * - Its end_time was yesterday (grace period for sets that just ended)
 * 
 * @param set Set with end_time field
 * @returns True if the set is upcoming (including grace period), false otherwise
 */
export function isSetUpcoming(set: ProcessedSet | { end_time?: string | null | undefined }): boolean {
	try {
		// Lineup-only sets (no end time) are considered upcoming
		if (!set?.end_time) {
			return true;
		}

		const now = new Date();
		const setEndTime = new Date(set.end_time);

		// Check if the set end time is valid (not NaN and reasonable year range)
		if (!Number.isNaN(setEndTime.getTime())) {
			// Additional check for reasonable date range (not default 1970 dates)
			const year = setEndTime.getFullYear();
			if (year >= 2020 && year <= 2030) {
				// Add 1 day grace period for sets that ended yesterday
				const dayAgo = new Date(now);
				dayAgo.setDate(dayAgo.getDate() - 1);
				dayAgo.setHours(0, 0, 0, 0);
				
				return setEndTime >= dayAgo;
			}
		}

		// For invalid dates, default to "upcoming" (true)
		// This prevents showing "past" status for sets with bad data
		return true;
	} catch (error) {
		console.error("Error checking if set is upcoming:", error);
		return true;
	}
}

/**
 * Check if a set has passed (end time is in the past)
 * Used for temporal awareness in UI text (e.g., "Want to See" vs "Wanted to See")
 * For lineup-only festivals, returns false (treat as upcoming) since we can't determine timing
 * 
 * @deprecated Use !isSetUpcoming(set) instead for consistency
 */
export function isSetPast(set: ProcessedSet): boolean {
	return !isSetUpcoming(set);
}

/**
 * Check if a festival has passed (end date is in the past)
 * Used to set appropriate default filters (e.g., "rated" for past festivals)
 */
export function isFestivalPast(endDate: string): boolean {
	try {
		const now = new Date();
		// Parse date without timezone conversion
		const [year, month, day] = endDate.split("-").map(Number);

		// Ensure all values are valid numbers
		if (!year || !month || !day) {
			return false; // If invalid date, assume not past
		}

		const festivalEndDate = new Date(year, month - 1, day);
		// Add one day buffer to account for timezone differences and overnight festivals
		festivalEndDate.setDate(festivalEndDate.getDate() + 1);
		return festivalEndDate < now;
	} catch (error) {
		console.error("Error checking if festival is past:", error);
		return false;
	}
}

/**
 * Format date to short format like "Jan 1, 2025"
 * Used for performance cards and other compact date displays
 * @param dateString Date string to format
 * @returns Formatted date string or empty string if invalid
 */
export function formatShortDate(dateString: string | null): string {
	if (!dateString) return "";

	try {
		const date = new Date(dateString);
		return date.toLocaleDateString("en-US", {
			month: "short",
			day: "numeric",
			year: "numeric",
		});
	} catch {
		return "";
	}
}

/**
 * Unified function to check if an event (festival or show) is upcoming
 * Includes a 1-day grace period after the event ends
 * 
 * @param startDate Start date (for festivals) or event date (for shows)
 * @param endDate Optional end date (for multi-day festivals). If not provided, uses startDate
 * @returns True if the event is upcoming (including grace period), false otherwise
 * 
 * Grace period logic:
 * - For festivals: End date + 1 day (overnight buffer)
 * - For shows: Event date + 1 day (shows from yesterday are still "upcoming")
 */
export function isUpcoming(
	startDate: string | null | undefined,
	endDate?: string | null | undefined,
): boolean {
	// If no start date, can't determine status
	if (!startDate) return false;

	try {
		const today = new Date();
		today.setHours(0, 0, 0, 0);

		// Use end date if provided (for festivals), otherwise use start date (for shows)
		const eventEndDate = endDate || startDate;
		const endDateObj = new Date(eventEndDate);
		
		// Add 1 day grace period for all events
		// This accounts for:
		// - Overnight festivals that technically end the next morning
		// - Shows that happened yesterday but are still relevant for "now" tab
		endDateObj.setDate(endDateObj.getDate() + 1);
		endDateObj.setHours(23, 59, 59, 999);

		return endDateObj >= today;
	} catch (error) {
		console.error("Error checking if event is upcoming:", error);
		return false;
	}
}

/**
 * Convenience function for checking if a show is upcoming
 * Delegates to the unified isUpcoming function
 * @deprecated Use isUpcoming(showDate) instead
 */
export function isShowUpcoming(showDate: string | null | undefined): boolean {
	return isUpcoming(showDate);
}
